import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { AuthContext } from '../App';
import { toast } from 'sonner';

const ConfigAnuncioDashboard = () => {
  const { API } = useContext(AuthContext);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);

  const [config, setConfig] = useState({
    activo: false,
    titulo: '',
    titulo_color: '#1e3a8a',
    descripcion: '',
    descripcion_color: '#374151',
    fondo_color: '#f0f9ff',
    borde_color: '#3b82f6',
    audiencia: 'todos',
    tipo_media: 'ninguno',
    media_url: '',
    boton_activo: false,
    boton_texto: '',
    boton_link: '',
    boton_color: '#2563eb'
  });

  useEffect(() => {
    cargarConfig();
  }, []);

  const cargarConfig = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const res = await axios.get(`${API}/admin/anuncio-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      console.error('Error cargando config:', err);
    } finally {
      setCargando(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      await axios.put(`${API}/admin/anuncio-dashboard`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Anuncio actualizado correctamente');
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <i className="fas fa-spinner fa-spin text-3xl text-blue-500" />
      </div>
    );
  }

  // Preview component
  const Preview = () => {
    const bgColor = config.fondo_color || '#f0f9ff';
    const borderColor = config.borde_color || '#3b82f6';

    return (
      <div
        className="rounded-2xl p-5 border-l-4 overflow-hidden relative"
        style={{ backgroundColor: bgColor, borderLeftColor: borderColor }}
        data-testid="anuncio-preview"
      >
        <div className="flex flex-col gap-3">
          {config.titulo && (
            <div className="flex items-center gap-3">
              <i className="fas fa-bullhorn text-lg" style={{ color: borderColor }} />
              <h3 className="text-lg font-bold" style={{ color: config.titulo_color }}>
                {config.titulo}
              </h3>
            </div>
          )}
          {config.descripcion && (
            <p className="text-sm whitespace-pre-wrap" style={{ color: config.descripcion_color }}>
              {config.descripcion}
            </p>
          )}
          {config.tipo_media === 'imagen' && config.media_url && (
            <img src={config.media_url} alt="Preview" className="rounded-xl max-h-48 object-cover w-full" />
          )}
          {config.tipo_media === 'video' && config.media_url && (
            <div className="bg-gray-200 rounded-xl aspect-video flex items-center justify-center">
              <i className="fas fa-play-circle text-4xl text-gray-500" />
              <span className="ml-2 text-gray-500 text-sm">Video embebido</span>
            </div>
          )}
          {config.tipo_media === 'audio' && config.media_url && (
            <div className="flex items-center gap-3 p-3 bg-white/60 rounded-xl">
              <i className="fas fa-music text-lg" style={{ color: borderColor }} />
              <div className="h-8 flex-1 bg-gray-200 rounded-full" />
            </div>
          )}
          {config.boton_activo && config.boton_texto && (
            <div>
              <span
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-white text-sm font-semibold"
                style={{ backgroundColor: config.boton_color || '#2563eb' }}
              >
                {config.boton_texto}
                <i className="fas fa-arrow-right text-xs" />
              </span>
            </div>
          )}
        </div>
        {!config.titulo && !config.descripcion && !config.media_url && (
          <p className="text-gray-400 text-center py-8 italic">Vista previa del anuncio...</p>
        )}
        <div className="absolute top-0 right-0 w-20 h-20 rounded-full -mr-8 -mt-8 opacity-10" style={{ backgroundColor: borderColor }} />
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <i className="fas fa-bullhorn text-blue-600" />
            Anuncio del Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Configura un widget destacado que aparece en el Dashboard de los usuarios
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${config.activo ? 'text-green-600' : 'text-gray-400'}`}>
            {config.activo ? 'Visible' : 'Oculto'}
          </span>
          <Switch
            checked={config.activo}
            onCheckedChange={(v) => handleChange('activo', v)}
            data-testid="anuncio-activo-switch"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="space-y-6">
          {/* Contenido */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <i className="fas fa-pen text-blue-500" />
                Contenido
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Titulo</Label>
                <Input
                  value={config.titulo}
                  onChange={(e) => handleChange('titulo', e.target.value)}
                  placeholder="Ej: Inscripciones abiertas"
                  data-testid="anuncio-titulo-input"
                />
              </div>
              <div>
                <Label>Descripcion</Label>
                <Textarea
                  value={config.descripcion}
                  onChange={(e) => handleChange('descripcion', e.target.value)}
                  placeholder="Escribe el mensaje del anuncio..."
                  rows={4}
                  data-testid="anuncio-descripcion-input"
                />
              </div>
              <div>
                <Label>Audiencia</Label>
                <Select value={config.audiencia} onValueChange={(v) => handleChange('audiencia', v)}>
                  <SelectTrigger data-testid="anuncio-audiencia-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los usuarios</SelectItem>
                    <SelectItem value="estudiantes_pastores">Solo Estudiantes y Pastores</SelectItem>
                    <SelectItem value="profesores">Solo Profesores</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Multimedia */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <i className="fas fa-photo-video text-purple-500" />
                Multimedia
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Tipo de contenido</Label>
                <Select value={config.tipo_media} onValueChange={(v) => handleChange('tipo_media', v)}>
                  <SelectTrigger data-testid="anuncio-tipo-media-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ninguno">Sin multimedia</SelectItem>
                    <SelectItem value="imagen">Imagen (URL)</SelectItem>
                    <SelectItem value="video">Video (YouTube / Vimeo)</SelectItem>
                    <SelectItem value="audio">Audio (URL)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {config.tipo_media !== 'ninguno' && (
                <div>
                  <Label>
                    {config.tipo_media === 'imagen' && 'URL de la imagen'}
                    {config.tipo_media === 'video' && 'URL del video (YouTube o Vimeo)'}
                    {config.tipo_media === 'audio' && 'URL del audio (MP3, etc.)'}
                  </Label>
                  <Input
                    value={config.media_url}
                    onChange={(e) => handleChange('media_url', e.target.value)}
                    placeholder={
                      config.tipo_media === 'imagen' ? 'https://ejemplo.com/imagen.jpg' :
                      config.tipo_media === 'video' ? 'https://youtube.com/watch?v=...' :
                      'https://ejemplo.com/audio.mp3'
                    }
                    data-testid="anuncio-media-url-input"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Boton de accion */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <i className="fas fa-hand-pointer text-green-500" />
                Boton de Accion
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  checked={config.boton_activo}
                  onCheckedChange={(v) => handleChange('boton_activo', v)}
                  data-testid="anuncio-boton-activo-switch"
                />
                <Label className="mb-0">Mostrar boton</Label>
              </div>
              {config.boton_activo && (
                <>
                  <div>
                    <Label>Texto del boton</Label>
                    <Input
                      value={config.boton_texto}
                      onChange={(e) => handleChange('boton_texto', e.target.value)}
                      placeholder="Ej: Ver mas"
                      data-testid="anuncio-boton-texto-input"
                    />
                  </div>
                  <div>
                    <Label>Enlace del boton</Label>
                    <Input
                      value={config.boton_link}
                      onChange={(e) => handleChange('boton_link', e.target.value)}
                      placeholder="https://... o /app/pagina"
                      data-testid="anuncio-boton-link-input"
                    />
                  </div>
                  <div>
                    <Label>Color del boton</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={config.boton_color}
                        onChange={(e) => handleChange('boton_color', e.target.value)}
                        className="h-10 w-14 rounded cursor-pointer border"
                      />
                      <Input
                        value={config.boton_color}
                        onChange={(e) => handleChange('boton_color', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Colores */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <i className="fas fa-palette text-orange-500" />
                Colores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Fondo', field: 'fondo_color' },
                  { label: 'Borde', field: 'borde_color' },
                  { label: 'Titulo', field: 'titulo_color' },
                  { label: 'Descripcion', field: 'descripcion_color' },
                ].map(({ label, field }) => (
                  <div key={field}>
                    <Label className="text-xs">{label}</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config[field]}
                        onChange={(e) => handleChange(field, e.target.value)}
                        className="h-9 w-12 rounded cursor-pointer border"
                      />
                      <Input
                        value={config[field]}
                        onChange={(e) => handleChange(field, e.target.value)}
                        className="flex-1 text-xs"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          <Card className="sticky top-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <i className="fas fa-eye text-indigo-500" />
                Vista Previa
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-100 rounded-xl p-4">
                <Preview />
              </div>
              <p className="text-xs text-gray-400 mt-3 text-center">
                Asi se vera el anuncio en el Dashboard
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end pt-2 pb-8">
        <Button
          onClick={guardar}
          disabled={guardando}
          className="px-8 py-3 text-base"
          data-testid="anuncio-guardar-btn"
        >
          {guardando ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2" />
              Guardando...
            </>
          ) : (
            <>
              <i className="fas fa-save mr-2" />
              Guardar Anuncio
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ConfigAnuncioDashboard;
